/**
 * Shared utilities for customer account and payment status.
 * Single source of truth for "Account Status" (LawnHub account created or not)
 * and "Payment Status" (payment setup via Stripe) across provider, admin, and
 * any UI that displays these.
 */

import React from 'react';
import { CheckCircle, Email, Warning } from '@mui/icons-material';

/** Minimal customer shape used for status checks (ExistingCustomer subset). */
export interface CustomerStatusShape {
  linkedCustomerId?: string | { toString(): string };
  invitationId?: string;
  paymentStructure?: { isPaymentSetup?: boolean };
  paymentStatus?: string;
  paymentMethodDetails?: { hasActivePaymentMethod?: boolean };
}

export type AccountStatusResult = {
  type: 'account_created' | 'invited' | 'not_invited';
  label: string;
  color: 'success' | 'info' | 'error';
  icon: React.ReactElement;
  tooltip: string;
};

/**
 * Account-only status: whether the customer has created a LawnHub account.
 * Do not use payment info here. Use for "Account Status" column everywhere.
 */
export function getAccountStatus(customer: CustomerStatusShape): AccountStatusResult {
  const linked = !!customer.linkedCustomerId;
  if (linked) {
    return {
      type: 'account_created',
      label: 'Account Created',
      color: 'success',
      icon: React.createElement(CheckCircle),
      tooltip: 'Customer has created a LawnHub account.',
    };
  }
  if (customer.invitationId) {
    return {
      type: 'invited',
      label: 'Invited',
      color: 'info',
      icon: React.createElement(Email),
      tooltip: 'Invitation sent. Customer has not created an account yet.',
    };
  }
  return {
    type: 'not_invited',
    label: 'Not Invited',
    color: 'error',
    icon: React.createElement(Warning),
    tooltip: 'Customer has not been invited to create a LawnHub account.',
  };
}

/**
 * Single source of truth for payment setup. True if customer has payment
 * configured (Stripe). Uses paymentStructure, paymentStatus,
 * paymentMethodDetails, and optionally other properties for same customer
 * (multi-property) when allCustomers is provided.
 */
export function hasPaymentSetup(
  customer: CustomerStatusShape,
  allCustomers?: CustomerStatusShape[]
): boolean {
  if (!customer.linkedCustomerId) return false;
  const lid = typeof customer.linkedCustomerId === 'string'
    ? customer.linkedCustomerId
    : customer.linkedCustomerId.toString();
  const check = (c: CustomerStatusShape) =>
    c.paymentStructure?.isPaymentSetup === true ||
    c.paymentStatus === 'setup' ||
    c.paymentMethodDetails?.hasActivePaymentMethod === true;
  if (check(customer)) return true;
  if (Array.isArray(allCustomers)) {
    return allCustomers.some((c) => {
      const cid = c.linkedCustomerId == null ? '' : (typeof c.linkedCustomerId === 'string' ? c.linkedCustomerId : c.linkedCustomerId.toString());
      return cid === lid && check(c);
    });
  }
  return false;
}
